'use strict';

export default function initStudentStoriesCarousel() {
	const carousels = document.querySelectorAll('.student-stories-carousel');

	carousels.forEach((carousel) => {
		const track = carousel.querySelector('.student-stories-carousel__track');
		const dotsContainer = carousel.querySelector('.student-stories-carousel__dots');
		if (!track || !dotsContainer) {
			return;
		}

		const items = Array.from(track.querySelectorAll('.student-stories-carousel__item'));
		if (items.length === 0) {
			return;
		}

		if (items.length <= 1) {
			dotsContainer.style.display = 'none';
		}

		const prevBtn = carousel.querySelector('.student-stories-carousel__pagination-item--previous');
		const nextBtn = carousel.querySelector('.student-stories-carousel__pagination-item--next');

		// Gi hvert item et id slik at dot kan peke med aria-controls
		items.forEach((item, index) => {
			if (!item.id) {
				item.id = `carousel-item-${index}-${carousel.id || 'carousel'}`;
			}
		});

		dotsContainer.innerHTML = '';

		const dots = items.map((item, index) => {
			const dot = document.createElement('button');
			dot.type = 'button';
			dot.className = 'student-stories-carousel__dot';
			dot.setAttribute('aria-label', `Gå til elevhistorie ${index + 1}`);
			dot.setAttribute('aria-controls', item.id);
			dot.setAttribute('data-item-index', String(index));
			const vimeoId = item.getAttribute('data-vimeo-id');
			if (vimeoId) {
				dot.setAttribute('data-vimeo-id', vimeoId);
			}
			dot.addEventListener('click', () => {
				slideToIndex(index);
			});
			dotsContainer.appendChild(dot);
			return dot;
		});

		const updatePaginationButtons = (activeIndex) => {
			if (prevBtn) {
				prevBtn.disabled = activeIndex === 0;
				prevBtn.setAttribute('aria-disabled', activeIndex === 0 ? 'true' : 'false');
			}
			if (nextBtn) {
				nextBtn.disabled = activeIndex === items.length - 1;
				nextBtn.setAttribute('aria-disabled', activeIndex === items.length - 1 ? 'true' : 'false');
			}
		};

		const setActive = (activeIndex) => {
			dots.forEach((dot, index) => {
				const isActive = index === activeIndex;
				dot.classList.toggle('is-active', isActive);
				dot.setAttribute('aria-pressed', isActive ? 'true' : 'false');
			});
			updatePaginationButtons(activeIndex);
		};

		let animatingToIndex = null;

		const getViewportCenterIndex = () => {
			const trackRect = track.getBoundingClientRect();
			const viewCenter = trackRect.left + trackRect.width / 2;
			let bestIndex = 0;
			let bestDistance = Infinity;

			items.forEach((item, index) => {
				const itemRect = item.getBoundingClientRect();
				const itemCenter = itemRect.left + itemRect.width / 2;
				const distance = Math.abs(itemCenter - viewCenter);
				if (distance < bestDistance) {
					bestDistance = distance;
					bestIndex = index;
				}
			});
			return bestIndex;
		};

		const updateActiveFromScroll = () => {
			if (animatingToIndex !== null) return;
			setActive(getViewportCenterIndex());
		};

		const SNAP_DURATION = 340;
		const easeOutCubic = (t) => 1 - (1 - t) ** 3;

		/** scrollLeft som sentrerer `item` i sporet (layout-basert – mer stabilt enn offsetLeft + snap) */
		const getScrollLeftToCenterItem = (item) => {
			const trackRect = track.getBoundingClientRect();
			const itemRect = item.getBoundingClientRect();
			const itemCenter = itemRect.left + itemRect.width / 2;
			const trackCenter = trackRect.left + trackRect.width / 2;
			const delta = itemCenter - trackCenter;
			const maxScrollVal = track.scrollWidth - track.clientWidth;
			const next = track.scrollLeft + delta;
			return Math.max(0, Math.min(next, maxScrollVal));
		};

		const slideToIndex = (index) => {
			const target = items[index];
			if (!target) return;
			animatingToIndex = index;
			setActive(index);
			track.classList.add('is-animating');

			const targetScroll = getScrollLeftToCenterItem(target);
			const startScroll = track.scrollLeft;
			const distance = targetScroll - startScroll;
			const startTime = performance.now();

			const tick = (now) => {
				const elapsed = now - startTime;
				const progress = Math.min(elapsed / SNAP_DURATION, 1);
				const eased = easeOutCubic(progress);
				track.scrollLeft = startScroll + distance * eased;
				if (progress >= 1) {
					track.scrollLeft = Math.round(targetScroll);
					track.classList.remove('is-animating');
					animatingToIndex = null;
				} else {
					requestAnimationFrame(tick);
				}
			};
			requestAnimationFrame(tick);
		};

		let rafId = null;
		track.addEventListener('scroll', () => {
			if (rafId) cancelAnimationFrame(rafId);
			rafId = requestAnimationFrame(updateActiveFromScroll);
		}, { passive: true });

		updateActiveFromScroll();

		// Mousedrag: dra track horisontalt med musa, snap til nærmeste slide ved slipp
		const DRAG_THRESHOLD_PX = 55; // Først ved drag over denne avstanden bytter vi til neste/forrige; under snapper til nærmeste
		let isDragging = false;
		let startX = 0;
		let lastPageX = 0;
		let startScrollLeft = 0;

		const onDragStart = (e) => {
			if (e.button !== 0) return;
			if (e.target.closest('button, a, [role="button"]')) return;
			isDragging = true;
			startX = e.pageX;
			lastPageX = e.pageX;
			startScrollLeft = track.scrollLeft;
			track.classList.add('is-dragging');
			track.style.userSelect = 'none';
			track.style.cursor = 'grabbing';
		};

		const maxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);

		const onDragMove = (e) => {
			if (!isDragging) return;
			e.preventDefault();
			lastPageX = e.pageX;
			const nextScroll = startScrollLeft + (startX - e.pageX);
			track.scrollLeft = Math.max(0, Math.min(nextScroll, maxScroll()));
			// Ikke oppdater dot under drag – mindre layout, oppdateres ved slipp
		};

		const getNearestIndex = () => getViewportCenterIndex();

		const snapToNearest = () => {
			const nearest = getNearestIndex();
			const currentScroll = track.scrollLeft;
			const target = items[nearest];
			if (!target) return;
			const targetScroll = getScrollLeftToCenterItem(target);
			if (Math.abs(currentScroll - targetScroll) < 2) return;
			slideToIndex(nearest);
		};

		const snapAfterDrag = () => {
			const dragDelta = startX - lastPageX;
			const currentIndex = getNearestIndex();
			if (dragDelta > DRAG_THRESHOLD_PX && currentIndex < items.length - 1) {
				slideToIndex(currentIndex + 1);
			} else if (dragDelta < -DRAG_THRESHOLD_PX && currentIndex > 0) {
				slideToIndex(currentIndex - 1);
			} else {
				snapToNearest();
			}
		};

		const onDragEnd = () => {
			if (!isDragging) return;
			isDragging = false;
			track.classList.remove('is-dragging');
			track.style.userSelect = '';
			track.style.cursor = '';
			snapAfterDrag();
		};

		track.addEventListener('mousedown', onDragStart);
		document.addEventListener('mousemove', onDragMove);
		document.addEventListener('mouseup', onDragEnd);
		track.addEventListener('mouseleave', onDragEnd);

		if (prevBtn) {
			prevBtn.addEventListener('click', () => {
				const current = getNearestIndex();
				slideToIndex(Math.max(0, current - 1));
			});
		}
		if (nextBtn) {
			nextBtn.addEventListener('click', () => {
				const current = getNearestIndex();
				slideToIndex(Math.min(items.length - 1, current + 1));
			});
		}

		// Play: oppdater iframe src til autoplay så videoen spilles i karusellvinduet
		items.forEach((item) => {
			const vimeoId = item.getAttribute('data-vimeo-id');
			if (!vimeoId) return;
			const videoWrap = item.querySelector('.student-stories-carousel__video');
			const iframe = item.querySelector('.student-stories-carousel__poster-video');
			const playBtn = item.querySelector('.student-stories-carousel__play');
			if (!videoWrap || !iframe || !playBtn) return;

			playBtn.addEventListener('click', (e) => {
				e.preventDefault();
				e.stopPropagation();
				if (videoWrap.classList.contains('is-playing')) return;
				const sep = iframe.src.includes('?') ? '&' : '?';
				iframe.src = iframe.src + sep + 'autoplay=1';
				iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
				videoWrap.classList.add('is-playing');
			});

			playBtn.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					playBtn.click();
				}
			});
		});
	});
}
